import React from 'react';
import FocusLock from 'react-focus-lock';
import { getContext, isAnimationDisabled } from 'common';
import { getElevationStyle, ElevationProps } from 'components/Elevation';
import { zIndex } from 'styles';

import AnimatedModalMask from './Animated.ModalMask';
import AnimatedSideDrawerContainer from './Animated.SideDrawerContainer';
import SideDrawerItem from './SideDrawerItem';
import SideDrawerItemDivider from './SideDrawerItemDivider';
import SideDrawerButton from './SideDrawerButton';
import SideDrawerList from './SideDrawerList';
import {
  NAVIGATION_DRAWER_WIDTH,
  COMPACT_NAVIGATION_DRAWER_WIDTH,
} from './styles';

const TEMPORARY_DRAWER_ELEVATION_INDEX = 16;
const PERSISTENT_DRAWER_ELEVATION_INDEX = 0;
const OPENED_COMPACT_DRAWER_ELEVATION_INDEX = 16;
const CLOSED_COMPACT_DRAWER_ELEVATION_INDEX = 0;

const DRAWER_STYLE = (variant, isVisible) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'space-between',
  width: (variant === 'compact' && !isVisible) ? COMPACT_NAVIGATION_DRAWER_WIDTH : NAVIGATION_DRAWER_WIDTH,
  maxHeight: '100%',
  minHeight: '100%',
  height: '100%',
  zIndex: `${zIndex.SIDE_DRAWER}`,
});

const DEFAULT_DRAWER_STYLE = (variant, isVisible) => (variant === 'temporary') || (variant === 'compact') ? ({
  ...DRAWER_STYLE(variant, isVisible),
  position: 'absolute' as const,
}) : DRAWER_STYLE(variant, isVisible);

const ELEVATION_STYLE = (variant, elevationProps, isVisible) => {
  let resolvedElevation = {};
  switch (variant) {
    case 'temporary':
      resolvedElevation = getElevationStyle({ elevationIndex: elevationProps.elevationIndex || TEMPORARY_DRAWER_ELEVATION_INDEX, dark: elevationProps.dark });
      break;
    case 'compact':
      resolvedElevation = getElevationStyle({
        elevationIndex: elevationProps.elevationIndex || isVisible ? OPENED_COMPACT_DRAWER_ELEVATION_INDEX : CLOSED_COMPACT_DRAWER_ELEVATION_INDEX,
        dark: elevationProps.dark
      });
      break;
    default:
      // persistent variant is default
      resolvedElevation = getElevationStyle({ elevationIndex: elevationProps.elevationIndex || PERSISTENT_DRAWER_ELEVATION_INDEX, dark: elevationProps.dark });
  }
  return resolvedElevation;
};

const SideDrawerContext = React.createContext<any>({});

export interface SideDrawerProps extends React.ComponentPropsWithRef<'div'> {
  /** The style of the container, `width` always should be in px. */
  style?: {
    width?: number
  };
  /** The variant of the SideDrawer. */
  variant?: 'temporary' | 'persistent' | 'compact';
  /** If `false`, the component will be hidden. */
  visible?: boolean;
  /** If `true`, the focus will be trapped in the SideDrawerList. */
  trapFocus?: boolean;
  /** To alter modal element attributes. */
  modalMaskProps?: {};
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Override the Elevation properties. See Elevation component for details. */
  elevationProps?: Partial<ElevationProps>;
  /** The content of the component, consider using `SideDrawerList` component. */
  children: React.ReactNode;
}

/** `SideDrawer` is made to be used in `AppBody` component. All other props than `SideDrawer`'s props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const SideDrawer = React.forwardRef<HTMLElement, SideDrawerProps>((props: SideDrawerProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    style,
    children,
    variant,
    visible,
    modalMaskProps,
    disableAnimation: propsdisableAnimation,
    elevationProps,
    trapFocus,
    ...otherProps
  } = props;
  const listTheme = theme.LIST.VERTICAL;
  const borderStyle = RTL ?
    { borderLeft: `0.0625rem solid  ${(listTheme.divider) as string}` }
    :
    { borderRight: `0.0625rem solid  ${(listTheme.divider) as string}` };

  const elevationStyle: any = ELEVATION_STYLE(variant, elevationProps, visible);
  const resolvedStyle = {
    ...DEFAULT_DRAWER_STYLE(variant, visible),
    ...elevationStyle,
    ...borderStyle,
    ...style,
  };
  const resolvedDisabledAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
  let isFocusTrapped = visible && ((variant === 'temporary') || (variant === 'compact'));
  if (trapFocus !== undefined) {
    isFocusTrapped = trapFocus;
  }

  return (
    <>
      <FocusLock disabled={!isFocusTrapped}>
        <AnimatedSideDrawerContainer ref={ref} variant={variant} disableAnimation={resolvedDisabledAnimation} {...otherProps} style={resolvedStyle} visible={visible}>
          <SideDrawerContext.Provider value={{ variant, visible, background: elevationStyle.backgroundColor, disableAnimation: resolvedDisabledAnimation }}>
            {children}
          </SideDrawerContext.Provider>
        </AnimatedSideDrawerContainer>
      </FocusLock>
      {variant === 'temporary' && <AnimatedModalMask {...modalMaskProps} disableAnimation={resolvedDisabledAnimation} visible={visible} />}
      {variant === 'compact' && visible && <AnimatedModalMask {...modalMaskProps} disableAnimation={resolvedDisabledAnimation} visible={visible} />}
    </>
  );
}));

SideDrawer.displayName = 'SideDrawer';

SideDrawer.defaultProps = {
  visible: true,
  variant: 'persistent',
  modalMaskProps: {},
  disableAnimation: undefined,
  style: {},
  elevationProps: { dark: true },
  trapFocus: undefined,
};

export {
  SideDrawer as default,
  SideDrawerList,
  SideDrawerButton,
  SideDrawerItem,
  SideDrawerItemDivider,
  SideDrawerContext,
};

